import {
  AdviceProductDetailsFormValues,
  WorkSituation,
} from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import {
  CoverageNeed,
  ProductCoverageName,
  PurchaseRestriction,
  PurchaseRestrictionReason,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  getPayment,
  getPaymentRestriction,
} from "Store/akaAdvice/interfaces/getters";

import { OneTimePaymentAdvisorChoice } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/interfaces";
import { NumericOption } from "Components/Form/RadioButton/radioTypes";
import { VALUE_TYPE } from "Components/NumberValue";
import { getPriceFormatFromValue } from "Formatting/index";
import { UserAuthorizationLevel } from "Store/advice/interfaces";
import { AkaAdvicePriceFormValues } from "../../../interfaces";
import { DISABILITY_CAPITAL_DOCUMENTATION_THRESHOLD } from "../disabilityConstants";

export const isWithinValidOtpRange = (
  twoTimesIncome: number,
  value: number,
  need: CoverageNeed,
): boolean => {
  const paymentRestriction = getPaymentRestriction(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  return (
    value >= paymentRestriction.minPayouts.PRODUCT_MANAGER &&
    value <= paymentRestriction.maxPayouts.PRODUCT_MANAGER &&
    value <= twoTimesIncome
  );
};

const getCorrectOTPValueBasedOnChoice = (
  oneTimePaymentValue: number,
  advisorValueNumber?: number,
  advisorChoice?: OneTimePaymentAdvisorChoice,
): number => {
  switch (advisorChoice) {
    case OneTimePaymentAdvisorChoice.ADVICED_ONE_TIME_PAYMENT:
      return oneTimePaymentValue;
    case OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT:
      return advisorValueNumber ?? 0;
    case OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT:
    default:
      return 0;
  }
};

export const hasExceededDocumentationThreshold = (
  oneTimePaymentValue: number,
  need: CoverageNeed,
  formValues: AkaAdvicePriceFormValues,
): boolean => {
  const paymentRestriction = getPaymentRestriction(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  const value = getCorrectOTPValueBasedOnChoice(
    oneTimePaymentValue,
    formValues.disabilityOneTimePaymentAdvisorValue,
    formValues.disabilityOneTimePaymentAdvisorChoice,
  );
  return (
    value > DISABILITY_CAPITAL_DOCUMENTATION_THRESHOLD &&
    value <= paymentRestriction.maxPayouts.AUTHORIZED_ADVISOR
  );
};

export const hasExceededMaxValueForAuthorizedAdvisor = (
  oneTimePaymentValue: number,
  need: CoverageNeed,
  formValues: AkaAdvicePriceFormValues,
): boolean => {
  const paymentRestriction = getPaymentRestriction(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  const value = getCorrectOTPValueBasedOnChoice(
    oneTimePaymentValue,
    formValues.disabilityOneTimePaymentAdvisorValue,
    formValues.disabilityOneTimePaymentAdvisorChoice,
  );
  return (
    value > paymentRestriction.maxPayouts.AUTHORIZED_ADVISOR &&
    value <= paymentRestriction.maxPayouts.PRODUCT_MANAGER
  );
};

export const getDisabilityPaymentAfter3MonthsHint = (
  eligibleForPaymentAfter3Months: boolean,
  adviceProductIntroFormValues: AdviceProductDetailsFormValues,
  oneTimePayment: any,
  lang: string,
): string => {
  if (eligibleForPaymentAfter3Months) {
    if (
      adviceProductIntroFormValues.workSituation === WorkSituation.WORK_PRIVATE
    ) {
      return `${oneTimePayment.advisorTexts.workSituation.sharedInfo[lang]} ${oneTimePayment.advisorTexts.workSituation.workPrivateInfo[lang]}`;
    } else if (
      adviceProductIntroFormValues.workSituation === WorkSituation.SELF_EMPLOYED
    ) {
      return `${oneTimePayment.advisorTexts.workSituation.sharedInfo[lang]} ${oneTimePayment.advisorTexts.workSituation.selfEmployedInfo[lang]}`;
    }
  }
  return oneTimePayment.advisorTexts.workSituation.sharedInfo[lang];
};

export const isCustomerOver50AndCanNotHaveOTP = (
  need: CoverageNeed,
): boolean => {
  const payment = getPayment(need, ProductCoverageName.DISABILITY_FLEX_CAPITAL);
  return (
    payment.restrictions.purchaseRestriction ===
      PurchaseRestriction.NOT_ALLOWED &&
    payment.restrictions.restrictionReasons.includes(
      PurchaseRestrictionReason.AGE,
    )
  );
};

export const hasDisabilityCapitalCustomerOptions = (
  need: CoverageNeed,
): boolean => {
  const restrictions = getPaymentRestriction(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  const minAllowed = restrictions.minPayouts[UserAuthorizationLevel.CUSTOMER];
  const maxAllowed = restrictions.maxPayouts[UserAuthorizationLevel.CUSTOMER];
  return minAllowed !== maxAllowed;
};

export const getDisabilityCapitalCustomerOptions = (
  need: CoverageNeed,
): NumericOption[] => {
  const restrictions = getPaymentRestriction(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );
  const minPayment = restrictions.minPayouts[UserAuthorizationLevel.CUSTOMER];
  const maxPayment = restrictions.maxPayouts[UserAuthorizationLevel.CUSTOMER];
  return [
    {
      id: String(minPayment),
      value: minPayment,
      label: getPriceFormatFromValue(minPayment, VALUE_TYPE.NOK),
    },
    {
      id: String(maxPayment),
      value: maxPayment,
      label: getPriceFormatFromValue(maxPayment, VALUE_TYPE.NOK),
    },
  ];
};
